import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import {
    config,
} from "../../utils/apiUrl";

/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
    lastReversedPayment:
        null,
    invoice:
        null,

    payments:
        [],

    selectedPayment:
        null,

    summary: {
        grandTotal: 0,
        storedPaidAmount: 0,
        calculatedPaidAmount: 0,
        balanceAmount: 0,
        paymentCount: 0,

        totalAllocated: 0,
        totalReversed: 0,
        totalAvailable: 0,
    },

    pagination: {
        page:
            1,

        limit:
            20,

        total:
            0,

        totalPages:
            1,

        hasNextPage:
            false,

        hasPreviousPage:
            false,
    },

    filters: {
        includeReversed:
            false,
    },

    loading: {
        history:
            false,

        details:
            false,

        reversal:
            false,
    },

    error: {
        history:
            "",

        details:
            "",

        reversal:
            "",
    },
};

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const getErrorMessage = (
    error,
    fallback
) => {
    return (
        error?.response?.data
            ?.message ||
        error?.data?.message ||
        error?.message ||
        fallback
    );
};

const getResponseData = (
    response
) => {
    return (
        response?.data ||
        response ||
        {}
    );
};

const getPaginationData = (
    response
) => {
    return (
        response?.meta ||
        response?.data
            ?.pagination ||
        response?.pagination ||
        {}
    );
};

const normalizePagination = (
    pagination = {},
    fallback = {}
) => {
    const page =
        Number(
            pagination.page ??
            pagination.currentPage ??
            fallback.page ??
            1
        );

    const limit =
        Number(
            pagination.limit ??
            pagination.pageSize ??
            fallback.limit ??
            20
        );

    const total =
        Number(
            pagination.total ??
            pagination.totalRecords ??
            fallback.total ??
            0
        );

    const totalPages =
        Number(
            pagination.totalPages ??
            Math.max(
                Math.ceil(
                    total /
                    Math.max(
                        limit,
                        1
                    )
                ),
                1
            )
        );

    return {
        page:
            Number.isFinite(
                page
            )
                ? page
                : 1,

        limit:
            Number.isFinite(
                limit
            )
                ? limit
                : 20,

        total:
            Number.isFinite(
                total
            )
                ? total
                : 0,

        totalPages:
            Number.isFinite(
                totalPages
            )
                ? totalPages
                : 1,

        hasNextPage:
            pagination
                .hasNextPage ??
            page <
            totalPages,

        hasPreviousPage:
            pagination
                .hasPreviousPage ??
            page >
            1,
    };
};

const cleanQueryParams = (
    params = {}
) => {
    const cleaned =
        {};

    Object.entries(
        params
    ).forEach(
        ([
            key,
            value,
        ]) => {
            if (
                value ===
                undefined ||
                value ===
                null ||
                value ===
                ""
            ) {
                return;
            }

            cleaned[key] =
                value;
        }
    );

    return cleaned;
};

const createQueryString = (
    params = {}
) => {
    const query =
        new URLSearchParams();

    Object.entries(
        cleanQueryParams(
            params
        )
    ).forEach(
        ([
            key,
            value,
        ]) => {
            query.set(
                key,
                String(value)
            );
        }
    );

    const queryString =
        query.toString();

    return queryString
        ? `?${queryString}`
        : "";
};


/*
|--------------------------------------------------------------------------
| PAYMENT NORMALIZATION HELPERS
|--------------------------------------------------------------------------
*/

const toMoney = (
    value,
    fallback = 0
) => {
    const parsedValue =
        Number(value);

    if (
        !Number.isFinite(
            parsedValue
        )
    ) {
        return fallback;
    }

    return (
        Math.round(
            (
                parsedValue +
                Number.EPSILON
            ) *
            100
        ) / 100
    );
};

const getRelationId = (
    value
) => {
    if (!value) {
        return null;
    }

    if (
        typeof value ===
        "object"
    ) {
        return (
            value?._id ||
            value?.id ||
            null
        );
    }

    return value;
};

const firstDefinedValue = (
    values = [],
    fallback = null
) => {
    for (
        const value of values
    ) {
        if (
            value !== undefined &&
            value !== null &&
            value !== ""
        ) {
            return value;
        }
    }

    return fallback;
};

const firstPositiveMoney = (
    values = [],
    fallback = 0
) => {
    for (
        const value of values
    ) {
        const parsedValue =
            toMoney(
                value,
                Number.NaN
            );

        if (
            Number.isFinite(
                parsedValue
            ) &&
            parsedValue > 0
        ) {
            return parsedValue;
        }
    }

    return fallback;
};

const normalizePaymentAllocation = (
    allocation = {},
    fallback = {}
) => {
    const document =
        allocation?.document &&
            typeof allocation.document ===
            "object"
            ? allocation.document
            : fallback?.document ||
            {};

    const allocatedAmount =
        toMoney(
            allocation
                ?.allocatedAmount ??
            fallback
                ?.allocatedAmount
        );

    const reversedAmount =
        toMoney(
            allocation
                ?.reversedAmount ??
            fallback
                ?.reversedAmount
        );

    const activeAmount =
        allocation?.activeAmount !==
            undefined &&
            allocation?.activeAmount !==
            null
            ? toMoney(
                allocation
                    .activeAmount
            )
            : Math.max(
                toMoney(
                    allocatedAmount -
                    reversedAmount
                ),
                0
            );

    return {
        ...fallback,
        ...allocation,

        _id:
            allocation?._id ||
            fallback?._id ||
            null,

        payment:
            getRelationId(
                allocation
                    ?.payment
            ) ||
            getRelationId(
                fallback
                    ?.payment
            ),

        document: {
            ...(
                fallback
                    ?.document ||
                {}
            ),

            ...document,

            _id:
                getRelationId(
                    document
                ) ||
                getRelationId(
                    fallback
                        ?.document
                ),

            documentNumber:
                document
                    ?.documentNumber ||
                allocation
                    ?.documentNumber ||
                fallback
                    ?.documentNumber ||
                "",

            documentDate:
                document
                    ?.documentDate ||
                fallback
                    ?.document
                    ?.documentDate ||
                null,
        },

        documentNumber:
            allocation
                ?.documentNumber ||
            document
                ?.documentNumber ||
            fallback
                ?.documentNumber ||
            "",

        allocatedAmount,

        reversedAmount,

        activeAmount,

        allocationDate:
            allocation
                ?.allocationDate ||
            fallback
                ?.allocationDate ||
            allocation
                ?.createdAt ||
            fallback
                ?.createdAt ||
            null,

        status:
            allocation
                ?.status ||
            fallback
                ?.status ||
            "ACTIVE",

        customer:
            getRelationId(
                allocation
                    ?.customer
            ) ||
            getRelationId(
                fallback
                    ?.customer
            ),

        websiteUser:
            getRelationId(
                allocation
                    ?.websiteUser
            ) ||
            getRelationId(
                fallback
                    ?.websiteUser
            ),
    };
};

const normalizeSaleInvoicePayment = (
    paymentRow = {},
    invoice = {}
) => {
    const nestedPayment =
        paymentRow?.payment &&
            typeof paymentRow.payment ===
            "object"
            ? paymentRow.payment
            : {};

    const nestedAllocation =
        paymentRow?.allocation &&
            typeof paymentRow.allocation ===
            "object"
            ? paymentRow.allocation
            : {};

    const paymentId =
        getRelationId(
            nestedPayment
        ) ||
        paymentRow
            ?.paymentId ||
        getRelationId(
            paymentRow
                ?.payment
        ) ||
        paymentRow?._id ||
        null;

    const allocationId =
        paymentRow
            ?.allocationId ||
        nestedAllocation
            ?._id ||
        null;

    const invoiceCustomerId =
        getRelationId(
            invoice
                ?.customer
        ) ||
        invoice
            ?.customerId ||
        invoice
            ?.customerSnapshot
            ?.customerId ||
        invoice
            ?.customerSnapshot
            ?._id ||
        null;

    const invoiceWebsiteUserId =
        getRelationId(
            invoice
                ?.websiteUser
        ) ||
        invoice
            ?.websiteUserId ||
        null;

    const customerId =
        getRelationId(
            nestedPayment
                ?.customer
        ) ||
        getRelationId(
            paymentRow
                ?.customer
        ) ||
        paymentRow
            ?.customerId ||
        invoiceCustomerId ||
        null;

    const websiteUserId =
        getRelationId(
            nestedPayment
                ?.websiteUser
        ) ||
        getRelationId(
            paymentRow
                ?.websiteUser
        ) ||
        paymentRow
            ?.websiteUserId ||
        invoiceWebsiteUserId ||
        null;

    const customerSnapshot = {
        ...(
            invoice
                ?.customerSnapshot ||
            {}
        ),

        ...(
            nestedPayment
                ?.customerSnapshot ||
            {}
        ),

        ...(
            paymentRow
                ?.customerSnapshot ||
            {}
        ),

        customerName:
            paymentRow
                ?.customerName ||
            nestedPayment
                ?.customerName ||
            paymentRow
                ?.customerSnapshot
                ?.customerName ||
            nestedPayment
                ?.customerSnapshot
                ?.customerName ||
            invoice
                ?.customerSnapshot
                ?.customerName ||
            invoice
                ?.customerSnapshot
                ?.name ||
            "",

        mobile:
            paymentRow
                ?.customerMobile ||
            nestedPayment
                ?.customerMobile ||
            paymentRow
                ?.customerSnapshot
                ?.mobile ||
            nestedPayment
                ?.customerSnapshot
                ?.mobile ||
            invoice
                ?.customerSnapshot
                ?.mobile ||
            "",

        email:
            paymentRow
                ?.customerEmail ||
            nestedPayment
                ?.customerEmail ||
            paymentRow
                ?.customerSnapshot
                ?.email ||
            nestedPayment
                ?.customerSnapshot
                ?.email ||
            invoice
                ?.customerSnapshot
                ?.email ||
            "",
    };

    const documentSource =
        paymentRow?.document &&
            typeof paymentRow.document ===
            "object"
            ? paymentRow.document
            : {};

    const document = {
        ...documentSource,

        _id:
            getRelationId(
                documentSource
            ) ||
            paymentRow
                ?.documentId ||
            invoice?._id ||
            null,

        documentType:
            documentSource
                ?.documentType ||
            invoice
                ?.documentType ||
            "SALE_INVOICE",

        documentNumber:
            documentSource
                ?.documentNumber ||
            paymentRow
                ?.documentNumber ||
            invoice
                ?.documentNumber ||
            "",

        documentDate:
            documentSource
                ?.documentDate ||
            invoice
                ?.documentDate ||
            null,

        dueDate:
            documentSource
                ?.dueDate ||
            invoice
                ?.dueDate ||
            null,
    };

    const allocatedAmount =
        toMoney(
            firstDefinedValue(
                [
                    paymentRow
                        ?.allocatedAmount,

                    nestedPayment
                        ?.allocatedAmount,

                    nestedAllocation
                        ?.allocatedAmount,

                    paymentRow
                        ?.activeAmount,
                ],
                0
            )
        );

    const reversedAmount =
        toMoney(
            firstDefinedValue(
                [
                    paymentRow
                        ?.reversedAmount,

                    nestedPayment
                        ?.reversedAmount,

                    nestedAllocation
                        ?.reversedAmount,
                ],
                0
            )
        );

    const activeAmount =
        paymentRow?.activeAmount !==
            undefined &&
            paymentRow?.activeAmount !==
            null
            ? toMoney(
                paymentRow
                    .activeAmount
            )
            : nestedAllocation
                ?.activeAmount !==
                undefined &&
                nestedAllocation
                    ?.activeAmount !==
                null
                ? toMoney(
                    nestedAllocation
                        .activeAmount
                )
                : Math.max(
                    toMoney(
                        allocatedAmount -
                        reversedAmount
                    ),
                    0
                );

    const amount =
        firstPositiveMoney(
            [
                paymentRow
                    ?.amount,

                paymentRow
                    ?.paymentAmount,

                paymentRow
                    ?.receivedAmount,

                nestedPayment
                    ?.amount,

                nestedPayment
                    ?.paymentAmount,

                nestedPayment
                    ?.receivedAmount,

                toMoney(
                    nestedPayment
                        ?.allocatedAmount
                ) +
                toMoney(
                    nestedPayment
                        ?.unallocatedAmount
                ),

                allocatedAmount,
            ],
            0
        );

    const explicitUnallocatedAmount =
        firstDefinedValue(
            [
                paymentRow
                    ?.unallocatedAmount,

                paymentRow
                    ?.availableAmount,

                nestedPayment
                    ?.unallocatedAmount,
            ],
            undefined
        );

    const unallocatedAmount =
        explicitUnallocatedAmount !==
            undefined
            ? Math.max(
                toMoney(
                    explicitUnallocatedAmount
                ),
                0
            )
            : Math.max(
                toMoney(
                    amount -
                    allocatedAmount
                ),
                0
            );

    const paymentStatus =
        String(
            paymentRow
                ?.paymentStatus ||
            nestedPayment
                ?.status ||
            paymentRow
                ?.status ||
            (
                unallocatedAmount <=
                    0
                    ? "FULLY_ALLOCATED"
                    : allocatedAmount >
                        0
                        ? "PARTIALLY_ALLOCATED"
                        : "COMPLETED"
            )
        )
            .trim()
            .toUpperCase();

    const allocationStatus =
        String(
            paymentRow
                ?.allocationStatus ||
            nestedAllocation
                ?.status ||
            "ACTIVE"
        )
            .trim()
            .toUpperCase();

    const isReversed =
        Boolean(
            paymentRow
                ?.isReversed ||
            nestedPayment
                ?.isReversed ||
            nestedPayment
                ?.reversal
                ?.isReversed
        ) ||
        paymentStatus ===
        "REVERSED" ||
        allocationStatus ===
        "REVERSED";

    const rawAllocations =
        Array.isArray(
            paymentRow
                ?.allocations
        )
            ? paymentRow
                .allocations
            : nestedAllocation &&
                Object.keys(
                    nestedAllocation
                ).length >
                0
                ? [
                    nestedAllocation,
                ]
                : [];

    const fallbackAllocation = {
        _id:
            allocationId,

        payment:
            paymentId,

        document,

        documentNumber:
            document
                ?.documentNumber ||
            "",

        allocatedAmount,

        reversedAmount,

        activeAmount,

        allocationDate:
            paymentRow
                ?.allocationDate ||
            paymentRow
                ?.paymentDate ||
            paymentRow
                ?.createdAt ||
            null,

        status:
            allocationStatus,

        customer:
            customerId,

        websiteUser:
            websiteUserId,
    };

    const allocations =
        rawAllocations.length >
            0
            ? rawAllocations.map(
                (
                    allocation
                ) =>
                    normalizePaymentAllocation(
                        allocation,
                        fallbackAllocation
                    )
            )
            : allocatedAmount >
                0
                ? [
                    normalizePaymentAllocation(
                        fallbackAllocation
                    ),
                ]
                : [];

    return {
        ...paymentRow,
        ...nestedPayment,

        _id:
            paymentId,

        id:
            paymentId,

        paymentId,

        allocationId,

        customer:
            customerId,

        customerId,

        websiteUser:
            websiteUserId,

        websiteUserId,

        customerSnapshot,

        customerName:
            customerSnapshot
                ?.customerName ||
            "",

        customerMobile:
            customerSnapshot
                ?.mobile ||
            "",

        customerEmail:
            customerSnapshot
                ?.email ||
            "",

        document,

        documentId:
            document?._id ||
            null,

        documentNumber:
            document
                ?.documentNumber ||
            "",

        receiptNumber:
            paymentRow
                ?.receiptNumber ||
            nestedPayment
                ?.receiptNumber ||
            "",

        paymentDate:
            paymentRow
                ?.paymentDate ||
            nestedPayment
                ?.paymentDate ||
            paymentRow
                ?.allocationDate ||
            paymentRow
                ?.createdAt ||
            null,

        paymentMethod:
            paymentRow
                ?.paymentMethod ||
            nestedPayment
                ?.paymentMethod ||
            "",

        paymentType:
            paymentRow
                ?.paymentType ||
            nestedPayment
                ?.paymentType ||
            "PAYMENT_IN",

        referenceNumber:
            paymentRow
                ?.referenceNumber ||
            nestedPayment
                ?.referenceNumber ||
            "",

        transactionId:
            paymentRow
                ?.transactionId ||
            nestedPayment
                ?.transactionId ||
            "",

        bankReferenceNumber:
            paymentRow
                ?.bankReferenceNumber ||
            nestedPayment
                ?.bankReferenceNumber ||
            "",

        upiTransactionId:
            paymentRow
                ?.upiTransactionId ||
            nestedPayment
                ?.upiTransactionId ||
            "",

        cardTransactionId:
            paymentRow
                ?.cardTransactionId ||
            nestedPayment
                ?.cardTransactionId ||
            "",

        chequeNumber:
            paymentRow
                ?.chequeNumber ||
            nestedPayment
                ?.chequeNumber ||
            "",

        chequeDate:
            paymentRow
                ?.chequeDate ||
            nestedPayment
                ?.chequeDate ||
            null,

        amount,

        paymentAmount:
            amount,

        receivedAmount:
            amount,

        allocatedAmount,

        reversedAmount,

        activeAmount:
            isReversed
                ? 0
                : activeAmount,

        unallocatedAmount:
            isReversed
                ? 0
                : unallocatedAmount,

        availableAmount:
            isReversed
                ? 0
                : unallocatedAmount,

        paymentStatus:

            isReversed
                ? "REVERSED"
                : paymentStatus,

        status:
            isReversed
                ? "REVERSED"
                : paymentStatus,

        allocationStatus:

            isReversed
                ? "REVERSED"
                : allocationStatus,

        isReversed,

        reversedAt:
            paymentRow
                ?.reversedAt ||
            nestedPayment
                ?.reversal
                ?.reversedAt ||
            nestedPayment
                ?.reversedAt ||
            null,

        reversalReason:
            paymentRow
                ?.reversalReason ||
            nestedPayment
                ?.reversal
                ?.reason ||
            nestedPayment
                ?.reversalReason ||
            "",

        allocations,

        notes:
            paymentRow
                ?.notes ||
            nestedPayment
                ?.notes ||
            "",

        createdAt:
            paymentRow
                ?.createdAt ||
            nestedPayment
                ?.createdAt ||
            null,

        updatedAt:
            paymentRow
                ?.updatedAt ||
            nestedPayment
                ?.updatedAt ||
            null,
    };
};

const normalizeSaleInvoicePaymentSummary = (
    summary = {},
    payments = [],
    invoice = {}
) => {
    const calculatedPaidAmount =
        toMoney(
            payments.reduce(
                (
                    total,
                    payment
                ) =>
                    total +
                    (
                        payment
                            ?.isReversed
                            ? 0
                            : toMoney(
                                payment
                                    ?.activeAmount
                            )
                    ),
                0
            )
        );

    const totalReversed =
        toMoney(
            payments.reduce(
                (
                    total,
                    payment
                ) =>
                    total +
                    toMoney(
                        payment
                            ?.reversedAmount
                    ),
                0
            )
        );

    const totalAllocated =
        toMoney(
            payments.reduce(
                (
                    total,
                    payment
                ) =>
                    total +
                    toMoney(
                        payment
                            ?.allocatedAmount
                    ),
                0
            )
        );

    const totalAvailable =
        toMoney(
            payments.reduce(
                (
                    total,
                    payment
                ) =>
                    total +
                    toMoney(
                        payment
                            ?.unallocatedAmount
                    ),
                0
            )
        );

    const grandTotal =
        toMoney(
            summary
                ?.grandTotal ??
            invoice
                ?.totals
                ?.grandTotal
        );

    const storedPaidAmount =
        toMoney(
            summary
                ?.storedPaidAmount ??
            invoice
                ?.totals
                ?.paidAmount ??
            calculatedPaidAmount
        );

    const balanceAmount =
        toMoney(
            summary
                ?.balanceAmount ??
            invoice
                ?.totals
                ?.balanceAmount ??
            Math.max(
                grandTotal -
                storedPaidAmount,
                0
            )
        );

    return {
        ...initialState.summary,
        ...summary,

        grandTotal,

        storedPaidAmount,

        calculatedPaidAmount:
            toMoney(
                summary
                    ?.calculatedPaidAmount ??
                calculatedPaidAmount
            ),

        balanceAmount,

        paymentCount:
            Number(
                summary
                    ?.paymentCount ??
                payments.filter(
                    (
                        payment
                    ) =>
                        payment
                            ?.isReversed !==
                        true &&
                        toMoney(
                            payment
                                ?.activeAmount
                        ) >
                        0
                ).length
            ) || 0,

        totalAllocated:
            toMoney(
                summary
                    ?.totalAllocated ??
                totalAllocated
            ),

        totalReversed:
            toMoney(
                summary
                    ?.totalReversed ??
                totalReversed
            ),

        totalAvailable:
            toMoney(
                summary
                    ?.totalAvailable ??
                totalAvailable
            ),
    };
};
/*
|--------------------------------------------------------------------------
| REVERSE PAYMENT
|--------------------------------------------------------------------------
*/

export const reverseSaleInvoicePayment =
    createAsyncThunk(
        "saleInvoicePayment/reversePayment",

        async (
            {
                paymentId,
                reason,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !paymentId
                ) {
                    return rejectWithValue(
                        "Payment ID is required."
                    );
                }

                const normalizedReason =
                    String(
                        reason ||
                        ""
                    ).trim();

                if (
                    !normalizedReason
                ) {
                    return rejectWithValue(
                        "Payment reversal reason is required."
                    );
                }

                const api =
                    new API();

                if (
                    typeof config
                        .reverseBillingPayment !==
                    "function"
                ) {
                    return rejectWithValue(
                        "Reverse payment API URL is not configured."
                    );
                }

                const response =
                    await api.patch(
                        config.reverseBillingPayment(
                            paymentId
                        ),
                        {
                            reason:
                                normalizedReason,

                            reversalReason:
                                normalizedReason,
                        }
                    );

                if (
                    response?.success ===
                    false ||
                    response?.status ===
                    "error"
                ) {
                    return rejectWithValue(
                        response?.message ||
                        "Unable to reverse payment."
                    );
                }

                const responseData =
                    getResponseData(
                        response
                    );

                return {
                    payment:
                        responseData
                            ?.payment ||
                        responseData,

                    message:
                        response?.message ||
                        "Payment reversed successfully.",
                };
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to reverse payment."
                    )
                );
            }
        }
    );
/*
|--------------------------------------------------------------------------
| FETCH PAYMENT HISTORY
|--------------------------------------------------------------------------
*/

export const fetchSaleInvoicePaymentHistory =
    createAsyncThunk(
        "saleInvoicePayment/fetchHistory",

        async (
            {
                invoiceId,
                page = 1,
                limit = 20,
                includeReversed = false,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !invoiceId
                ) {
                    return rejectWithValue(
                        "Sale invoice ID is required."
                    );
                }

                const api =
                    new API();

                if (
                    typeof config
                        .getSaleInvoicePaymentHistory !==
                    "function"
                ) {
                    return rejectWithValue(
                        "Payment history API URL is not configured."
                    );
                }

                const query =
                    createQueryString({
                        page,

                        limit,

                        includeReversed:
                            includeReversed
                                ? "true"
                                : undefined,
                    });

                const response =
                    await api.get(
                        `${config.getSaleInvoicePaymentHistory(
                            invoiceId
                        )}${query}`
                    );

                if (
                    response?.success ===
                    false ||
                    response?.status ===
                    "error"
                ) {
                    return rejectWithValue(
                        response?.message ||
                        "Unable to fetch invoice payment history."
                    );
                }

                const responseData =
                    getResponseData(
                        response
                    );

                const invoice =
                    responseData
                        ?.invoice ||
                    null;

                const rawPayments =
                    Array.isArray(
                        responseData
                            ?.payments
                    )
                        ? responseData
                            .payments
                        : [];

                const payments =
                    rawPayments.map(
                        (
                            payment
                        ) =>
                            normalizeSaleInvoicePayment(
                                payment,
                                invoice ||
                                {}
                            )
                    );

                const summary =
                    normalizeSaleInvoicePaymentSummary(
                        responseData
                            ?.summary ||
                        {},
                        payments,
                        invoice ||
                        {}
                    );

                return {
                    invoice,

                    payments,

                    summary,

                    pagination:
                        getPaginationData(
                            response
                        ),
                };
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to fetch invoice payment history."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH SINGLE PAYMENT ALLOCATION
|--------------------------------------------------------------------------
*/

export const fetchSaleInvoicePaymentById =
    createAsyncThunk(
        "saleInvoicePayment/fetchById",

        async (
            {
                invoiceId,
                allocationId,
            } = {},
            {
                rejectWithValue,
                getState,
            }
        ) => {
            try {
                if (
                    !invoiceId
                ) {
                    return rejectWithValue(
                        "Sale invoice ID is required."
                    );
                }

                if (
                    !allocationId
                ) {
                    return rejectWithValue(
                        "Payment allocation ID is required."
                    );
                }

                const api =
                    new API();

                if (
                    typeof config
                        .getSaleInvoicePaymentById !==
                    "function"
                ) {
                    return rejectWithValue(
                        "Payment details API URL is not configured."
                    );
                }

                const response =
                    await api.get(
                        config.getSaleInvoicePaymentById(
                            invoiceId,
                            allocationId
                        )
                    );

                if (
                    response?.success ===
                    false ||
                    response?.status ===
                    "error"
                ) {
                    return rejectWithValue(
                        response?.message ||
                        "Unable to fetch invoice payment."
                    );
                }

                const responseData =
                    getResponseData(
                        response
                    );

                const state =
                    getState();

                const invoice =
                    state
                        ?.saleInvoicePayment
                        ?.invoice ||
                    {};

                const payment =
                    responseData
                        ?.payment ||
                    responseData
                        ?.allocation ||
                    responseData ||
                    null;

                return payment
                    ? normalizeSaleInvoicePayment(
                        payment,
                        invoice
                    )
                    : null;
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to fetch invoice payment."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const saleInvoicePaymentSlice =
    createSlice({
        name:
            "saleInvoicePayment",

        initialState,

        reducers: {
            setSaleInvoicePaymentPage: (
                state,
                action
            ) => {
                state.pagination.page =
                    Number(
                        action.payload
                    ) || 1;
            },

            setSaleInvoicePaymentLimit: (
                state,
                action
            ) => {
                state.pagination.limit =
                    Number(
                        action.payload
                    ) || 20;

                state.pagination.page =
                    1;
            },

            setIncludeReversedPayments: (
                state,
                action
            ) => {
                state.filters.includeReversed =
                    Boolean(
                        action.payload
                    );

                state.pagination.page =
                    1;
            },

            setSelectedSaleInvoicePayment: (
                state,
                action
            ) => {
                state.selectedPayment =
                    action.payload ||
                    null;
            },

            clearSelectedSaleInvoicePayment: (
                state
            ) => {
                state.selectedPayment =
                    null;

                state.error.details =
                    "";
            },

            clearSaleInvoicePaymentHistory: (
                state
            ) => {
                state.invoice =
                    null;

                state.payments =
                    [];

                state.summary = {
                    ...initialState.summary,
                };

                state.pagination = {
                    ...initialState.pagination,
                };

                state.error.history =
                    "";
            },

            clearSaleInvoicePaymentErrors: (
                state
            ) => {
                state.error = {
                    history:
                        "",

                    details:
                        "",
                };
            },

            resetSaleInvoicePaymentState:
                () => ({
                    ...initialState,

                    summary: {
                        ...initialState.summary,
                    },

                    pagination: {
                        ...initialState.pagination,
                    },

                    filters: {
                        ...initialState.filters,
                    },

                    loading: {
                        ...initialState.loading,
                    },

                    error: {
                        ...initialState.error,
                    },
                }),

            clearLastReversedSaleInvoicePayment: (
                state
            ) => {
                state.lastReversedPayment =
                    null;

                state.error.reversal =
                    "";
            },
        },

        extraReducers: (
            builder
        ) => {
            builder

                /*
                |--------------------------------------------------------------------------
                | PAYMENT HISTORY
                |--------------------------------------------------------------------------
                */

                .addCase(
                    fetchSaleInvoicePaymentHistory
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.history =
                            true;

                        state.error.history =
                            "";
                    }
                )

                .addCase(
                    fetchSaleInvoicePaymentHistory
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.history =
                            false;

                        state.error.history =
                            "";

                        state.invoice =
                            action.payload
                                ?.invoice ||
                            null;

                        state.payments =
                            Array.isArray(
                                action.payload
                                    ?.payments
                            )
                                ? action.payload
                                    .payments
                                : [];

                        state.summary = {
                            ...initialState.summary,

                            ...action.payload
                                ?.summary,
                        };

                        state.pagination =
                            normalizePagination(
                                action.payload
                                    ?.pagination,
                                state.pagination
                            );

                        if (
                            state.selectedPayment
                                ?.paymentId
                        ) {
                            const selectedPayment =
                                state.payments.find(
                                    (
                                        payment
                                    ) =>
                                        String(
                                            payment
                                                ?.paymentId ||
                                            ""
                                        ) ===
                                        String(
                                            state
                                                .selectedPayment
                                                ?.paymentId ||
                                            ""
                                        )
                                );

                            state.selectedPayment =
                                selectedPayment ||
                                null;
                        }
                    }
                )

                .addCase(
                    fetchSaleInvoicePaymentHistory
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.history =
                            false;

                        state.error.history =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to fetch invoice payment history.";
                    }
                )

                /*
                |--------------------------------------------------------------------------
                | PAYMENT DETAILS
                |--------------------------------------------------------------------------
                */

                .addCase(
                    fetchSaleInvoicePaymentById
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.details =
                            true;

                        state.error.details =
                            "";

                        state.selectedPayment =
                            null;
                    }
                )

                .addCase(
                    fetchSaleInvoicePaymentById
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        state.error.details =
                            "";

                        state.selectedPayment =
                            action.payload ||
                            null;

                        if (
                            action.payload
                                ?.paymentId
                        ) {
                            const paymentIndex =
                                state.payments.findIndex(
                                    (
                                        payment
                                    ) =>
                                        String(
                                            payment
                                                ?.paymentId ||
                                            ""
                                        ) ===
                                        String(
                                            action
                                                .payload
                                                ?.paymentId ||
                                            ""
                                        )
                                );

                            if (
                                paymentIndex !==
                                -1
                            ) {
                                state.payments[
                                    paymentIndex
                                ] = {
                                    ...state.payments[
                                    paymentIndex
                                    ],

                                    ...action.payload,
                                };
                            }
                        }
                    }
                )

                .addCase(
                    fetchSaleInvoicePaymentById
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        state.error.details =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to fetch invoice payment.";
                    }
                )
                /*
|--------------------------------------------------------------------------
| REVERSE PAYMENT
|--------------------------------------------------------------------------
*/

                .addCase(
                    reverseSaleInvoicePayment
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.reversal =
                            true;

                        state.error.reversal =
                            "";

                        state.lastReversedPayment =
                            null;
                    }
                )
                .addCase(
                    reverseSaleInvoicePayment
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.reversal =
                            false;

                        state.error.reversal =
                            "";

                        const reversedPayment =
                            action.payload
                                ?.payment ||
                            null;

                        state.lastReversedPayment =
                            reversedPayment;

                        const paymentId =
                            getRelationId(
                                reversedPayment
                            ) ||
                            reversedPayment
                                ?.paymentId ||
                            null;

                        if (
                            paymentId
                        ) {
                            state.payments =
                                state.payments.map(
                                    (
                                        payment
                                    ) => {
                                        if (
                                            String(
                                                payment
                                                    ?.paymentId ||
                                                ""
                                            ) !==
                                            String(
                                                paymentId
                                            )
                                        ) {
                                            return payment;
                                        }

                                        const reversedAmount =
                                            toMoney(
                                                reversedPayment
                                                    ?.reversedAmount ??
                                                reversedPayment
                                                    ?.amount ??
                                                payment
                                                    ?.activeAmount ??
                                                payment
                                                    ?.amount
                                            );

                                        return {
                                            ...payment,

                                            ...reversedPayment,

                                            _id:
                                                payment
                                                    ?.paymentId,

                                            paymentId:
                                                payment
                                                    ?.paymentId,

                                            paymentStatus:
                                                "REVERSED",

                                            status:
                                                "REVERSED",

                                            allocationStatus:
                                                "REVERSED",

                                            isReversed:
                                                true,

                                            reversedAt:
                                                reversedPayment
                                                    ?.reversal
                                                    ?.reversedAt ||
                                                reversedPayment
                                                    ?.reversedAt ||
                                                new Date()
                                                    .toISOString(),

                                            reversalReason:
                                                reversedPayment
                                                    ?.reversal
                                                    ?.reason ||
                                                reversedPayment
                                                    ?.reversalReason ||
                                                "",

                                            reversedAmount,

                                            activeAmount:
                                                0,

                                            unallocatedAmount:
                                                0,

                                            availableAmount:
                                                0,

                                            allocations:
                                                Array.isArray(
                                                    payment
                                                        ?.allocations
                                                )
                                                    ? payment
                                                        .allocations
                                                        .map(
                                                            (
                                                                allocation
                                                            ) => ({
                                                                ...allocation,

                                                                status:
                                                                    "REVERSED",

                                                                reversedAmount:
                                                                    allocation
                                                                        ?.allocatedAmount ||
                                                                    allocation
                                                                        ?.activeAmount ||
                                                                    0,

                                                                activeAmount:
                                                                    0,
                                                            })
                                                        )
                                                    : [],
                                        };
                                    }
                                );
                        }

                        state.selectedPayment =
                            null;

                        state.summary =
                            normalizeSaleInvoicePaymentSummary(
                                state.summary,
                                state.payments,
                                state.invoice ||
                                {}
                            );
                    }
                )

                .addCase(
                    reverseSaleInvoicePayment
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.reversal =
                            false;

                        state.error.reversal =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to reverse payment.";
                    }
                );
        },
    });

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    setSaleInvoicePaymentPage,
    setSaleInvoicePaymentLimit,
    setIncludeReversedPayments,
    setSelectedSaleInvoicePayment,
    clearSelectedSaleInvoicePayment,
    clearSaleInvoicePaymentHistory,
    clearSaleInvoicePaymentErrors,
    resetSaleInvoicePaymentState,
    clearLastReversedSaleInvoicePayment,
} =
    saleInvoicePaymentSlice.actions;

/*
|--------------------------------------------------------------------------
| BASE SELECTOR
|--------------------------------------------------------------------------
*/

export const saleInvoicePaymentSelector =
    (
        state
    ) =>
        state
            ?.saleInvoicePayment ||
        initialState;

/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const saleInvoicePaymentInvoiceSelector =
    (
        state
    ) =>
        saleInvoicePaymentSelector(
            state
        ).invoice;

export const saleInvoicePaymentsSelector =
    (
        state
    ) =>
        saleInvoicePaymentSelector(
            state
        ).payments;

export const selectedSaleInvoicePaymentSelector =
    (
        state
    ) =>
        saleInvoicePaymentSelector(
            state
        ).selectedPayment;

export const saleInvoicePaymentSummarySelector =
    (
        state
    ) =>
        saleInvoicePaymentSelector(
            state
        ).summary;

export const saleInvoicePaymentPaginationSelector =
    (
        state
    ) =>
        saleInvoicePaymentSelector(
            state
        ).pagination;

export const saleInvoicePaymentFiltersSelector =
    (
        state
    ) =>
        saleInvoicePaymentSelector(
            state
        ).filters;

export const saleInvoicePaymentLoadingSelector =
    (
        state
    ) =>
        saleInvoicePaymentSelector(
            state
        ).loading;

export const saleInvoicePaymentErrorSelector =
    (
        state
    ) =>
        saleInvoicePaymentSelector(
            state
        ).error;

export const activeSaleInvoicePaymentsSelector =
    (
        state
    ) => {
        const payments =
            saleInvoicePaymentsSelector(
                state
            );

        return payments.filter(
            (
                payment
            ) =>
                payment
                    ?.activeAmount >
                0 &&
                payment
                    ?.isReversed !==
                true
        );
    };

export const reversedSaleInvoicePaymentsSelector =
    (
        state
    ) => {
        const payments =
            saleInvoicePaymentsSelector(
                state
            );

        return payments.filter(
            (
                payment
            ) =>
                payment
                    ?.isReversed ===
                true ||
                payment
                    ?.allocationStatus ===
                "REVERSED"
        );
    };

export const saleInvoicePaymentReversalLoadingSelector =
    (
        state
    ) =>
        Boolean(
            saleInvoicePaymentSelector(
                state
            ).loading
                ?.reversal
        );

export const saleInvoicePaymentReversalErrorSelector =
    (
        state
    ) =>
        saleInvoicePaymentSelector(
            state
        ).error
            ?.reversal ||
        "";

export const lastReversedSaleInvoicePaymentSelector =
    (
        state
    ) =>
        saleInvoicePaymentSelector(
            state
        ).lastReversedPayment;

/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default
    saleInvoicePaymentSlice.reducer;